import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface LeadSquaredMapping {
  leadSubBrand?: string;
  leadSourceDescription?: string;
  resortCode?: string;
  leadLocation?: string;
  resortCount?: number;
  isPartnerHotel?: boolean;
  dealType?: string;
}

interface Enquiry {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  dob: string;
  city: string;
  package: {
    _id: string;
    name: string;
    packageCode: string;
  };
  packageName: string;
  dealType?: 'normal' | 'lastMinute';
  marketingConsent: boolean;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  leadSquaredId?: string;
  leadSquaredStatus: 'pending' | 'synced' | 'failed';
  leadSquaredError?: string;
  leadSquaredMapping?: LeadSquaredMapping;
  source: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  fullName: string;
}

const AdminEnquiries: React.FC = () => {
  const { token } = useAuth();
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [resyncingLead, setResyncingLead] = useState(false);
  
  // Delete functionality
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  
  // Date filters
  const [dateFilter, setDateFilter] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchEnquiries();
    setSelectedIds([]); // Clear selection when filters change
  }, [currentPage, statusFilter, dateFilter, customStartDate, customEndDate]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`/api/enquiries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnquiries(response.data.enquiries || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'contacted': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'qualified': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'converted': return 'bg-green-100 text-green-800 border-green-200';
      case 'lost': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getLeadSquaredStatusIcon = (status: string) => {
    switch (status) {
      case 'synced': return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'failed': return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      case 'pending': return <ClockIcon className="w-4 h-4 text-yellow-600" />;
      default: return <ClockIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const updateEnquiryStatus = async (enquiryId: string, status: string, notes?: string) => {
    try {
      setUpdatingStatus(true);
      await axios.put(`/api/enquiries/${enquiryId}`, { status, notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchEnquiries();
      if (selectedEnquiry && selectedEnquiry._id === enquiryId) {
        setSelectedEnquiry({ ...selectedEnquiry, status: status as any, notes });
      }
    } catch (error) {
      console.error('Error updating enquiry:', error);
      alert('Failed to update enquiry status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const resyncLeadSquared = async (enquiryId: string) => {
    try {
      setResyncingLead(true);
      console.log('🔄 Resyncing enquiry to LeadSquared:', enquiryId);
      
      const response = await axios.post(`/api/enquiries/${enquiryId}/resync-leadsquared`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Resync response:', response.data);
      fetchEnquiries();
      alert(`LeadSquared sync successful! Lead ID: ${response.data.leadId || 'Created'}`);
    } catch (error: any) {
      console.error('❌ Error resyncing LeadSquared:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to sync with LeadSquared';
      alert(`LeadSquared sync failed: ${errorMessage}`);
    } finally {
      setResyncingLead(false);
    }
  };

  // Delete handlers
  const handleSelectAll = () => {
    if (selectedIds.length === filteredEnquiries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredEnquiries.map(e => e._id));
    }
  };

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDeleteSingle = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this enquiry? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await axios.delete(`/api/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnquiries(enquiries.filter(e => e._id !== id));
      setTotalCount(totalCount - 1);
      alert('Enquiry deleted successfully');
    } catch (error: any) {
      console.error('Error deleting enquiry:', error);
      alert(error.response?.data?.message || 'Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Please select enquiries to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} enquiry(ies)? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      const response = await axios.post('/api/enquiries/bulk-delete', 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      alert(response.data.message);
      setSelectedIds([]);
      fetchEnquiries();
    } catch (error: any) {
      console.error('Error bulk deleting enquiries:', error);
      alert(error.response?.data?.message || 'Failed to delete enquiries');
    } finally {
      setDeleting(false);
    }
  };

  // Apply date filter
  const getDateFilterRange = () => {
    const now = new Date();
    let startDate: Date | null = null;
    let endDate: Date = new Date();
    endDate.setHours(23, 59, 59, 999);

    switch (dateFilter) {
      case 'today':
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        if (customStartDate) startDate = new Date(customStartDate);
        if (customEndDate) {
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        return null;
    }

    return { startDate, endDate };
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      enquiry.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.packageName.toLowerCase().includes(searchTerm.toLowerCase());

    // Date filter
    const dateRange = getDateFilterRange();
    let matchesDate = true;
    if (dateRange && dateRange.startDate) {
      const enquiryDate = new Date(enquiry.createdAt);
      matchesDate = enquiryDate >= dateRange.startDate && enquiryDate <= dateRange.endDate;
    }

    return matchesSearch && matchesDate;
  });

  if (loading && enquiries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Enquiries - Admin | Bali Getaway</title>
      </Helmet>

      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer Enquiries</h1>
            <p className="text-gray-600 mt-2">
              Manage and track customer enquiries • Total: {totalCount}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-4">
            {/* First row: Search, Status, Date Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or package..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>

              {/* Date Filter */}
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Last 7 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Bulk Delete Button */}
              {selectedIds.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="w-5 h-5" />
                  Delete ({selectedIds.length})
                </button>
              )}
            </div>

            {/* Second row: Custom Date Range (shown when custom is selected) */}
            {dateFilter === 'custom' && (
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enquiries Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredEnquiries.length && filteredEnquiries.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LeadSquared
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(enquiry._id)}
                        onChange={() => handleSelectOne(enquiry._id)}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {enquiry.fullName}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <EnvelopeIcon className="w-3 h-3" />
                            {enquiry.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <PhoneIcon className="w-3 h-3" />
                            {enquiry.countryCode}{enquiry.phoneNumber}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {enquiry.packageName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {enquiry.package?.packageCode}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(enquiry.status)}`}>
                        {enquiry.status.charAt(0).toUpperCase() + enquiry.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getLeadSquaredStatusIcon(enquiry.leadSquaredStatus)}
                        <span className="text-sm text-gray-600 capitalize">
                          {enquiry.leadSquaredStatus}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {new Date(enquiry.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedEnquiry(enquiry);
                            setShowDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {enquiry.leadSquaredStatus === 'failed' && (
                          <button
                            onClick={() => resyncLeadSquared(enquiry._id)}
                            disabled={resyncingLead}
                            className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                            title="Resync to LeadSquared"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteSingle(enquiry._id)}
                          disabled={deleting}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Delete Enquiry"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 border rounded-md text-sm font-medium ${
                        currentPage === page
                          ? 'bg-orange-600 text-white border-orange-600'
                          : 'border-gray-300 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredEnquiries.length === 0 && !loading && (
          <div className="text-center py-12">
            <EnvelopeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No enquiries found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter ? 'Try adjusting your filters' : 'Enquiries will appear here when customers submit them'}
            </p>
          </div>
        )}

        {/* Enquiry Details Modal */}
        {showDetails && selectedEnquiry && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-orange-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">{selectedEnquiry.fullName}</h2>
                  <p className="text-yellow-100">Enquiry Details</p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-white hover:text-gray-200"
                >
                  ×
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Customer Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <p className="text-gray-900">{selectedEnquiry.fullName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="text-gray-900">{selectedEnquiry.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="text-gray-900">{selectedEnquiry.countryCode}{selectedEnquiry.phoneNumber}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <p className="text-gray-900">{selectedEnquiry.city}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                      <p className="text-gray-900">{new Date(selectedEnquiry.dob).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Marketing Consent</label>
                      <p className="text-gray-900">{selectedEnquiry.marketingConsent ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {/* Package Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Package Information</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-900">{selectedEnquiry.packageName}</p>
                    <p className="text-sm text-gray-600">Code: {selectedEnquiry.package?.packageCode}</p>
                  </div>
                </div>

                {/* Status & LeadSquared */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Status & Integration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={selectedEnquiry.status}
                        onChange={(e) => updateEnquiryStatus(selectedEnquiry._id, e.target.value)}
                        disabled={updatingStatus}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">LeadSquared Status</label>
                      <div className="flex items-center gap-2">
                        {getLeadSquaredStatusIcon(selectedEnquiry.leadSquaredStatus)}
                        <span className="capitalize">{selectedEnquiry.leadSquaredStatus}</span>
                        {selectedEnquiry.leadSquaredStatus === 'failed' && (
                          <button
                            onClick={() => resyncLeadSquared(selectedEnquiry._id)}
                            disabled={resyncingLead}
                            className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded hover:bg-orange-200 disabled:opacity-50"
                          >
                            Retry Sync
                          </button>
                        )}
                      </div>
                      {selectedEnquiry.leadSquaredError && (
                        <p className="text-xs text-red-600 mt-1">{selectedEnquiry.leadSquaredError}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* LeadSquared Mapping Details */}
                {selectedEnquiry.leadSquaredMapping && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">LeadSquared Mapping</h3>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-blue-700">Lead Sub Brand</label>
                          <p className="text-gray-900 font-medium">{selectedEnquiry.leadSquaredMapping.leadSubBrand || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700">Lead Source Description</label>
                          <p className="text-gray-900 font-medium">{selectedEnquiry.leadSquaredMapping.leadSourceDescription || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700">Resort Code</label>
                          <p className="text-gray-900 font-medium">{selectedEnquiry.leadSquaredMapping.resortCode || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700">Lead Location</label>
                          <p className="text-gray-900 font-medium">{selectedEnquiry.leadSquaredMapping.leadLocation || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700">Resort Count</label>
                          <p className="text-gray-900 font-medium">{selectedEnquiry.leadSquaredMapping.resortCount ?? '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700">Partner Hotel</label>
                          <p className="text-gray-900 font-medium">
                            {selectedEnquiry.leadSquaredMapping.isPartnerHotel ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Yes (KEPH)
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                No
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="col-span-2 mt-3 pt-3 border-t border-blue-200">
                        <label className="block text-xs font-medium text-blue-700">Deal Type (mx_General_Flag)</label>
                        <p className="text-gray-900 font-medium">
                          {selectedEnquiry.leadSquaredMapping.dealType || (selectedEnquiry.dealType === 'lastMinute' ? 'Last Minute Deal' : 'Normal Deal')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={selectedEnquiry.notes || ''}
                    onChange={(e) => {
                      const updatedEnquiry = { ...selectedEnquiry, notes: e.target.value };
                      setSelectedEnquiry(updatedEnquiry);
                    }}
                    onBlur={(e) => updateEnquiryStatus(selectedEnquiry._id, selectedEnquiry.status, e.target.value)}
                    placeholder="Add notes about this enquiry..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Created:</span> {new Date(selectedEnquiry.createdAt).toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Updated:</span> {new Date(selectedEnquiry.updatedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminEnquiries;
